import { Container, styled } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PoolBanner, PoolRow } from '@synergy/components-next';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { UserRole } from '@synergy/models';
import { hasRole } from '@synergy/utils';
import { useParams } from 'react-router';

import { PoolPageHeader } from '../../components/page-header/page-header';
import { SupplierTable } from './supplier-table/supplier-table';
import { WishListEmpty } from './wish-list-empty';
import { WishListId, WishListListItem } from '../../models/wish-list-list-item';
import { WishListModal } from './modals/wish-list-modal';
import { WishListSelection } from './wish-list-selection';
import { WishListTopActionBar } from './wish-list-top-action-bar';
import { WishListType } from '../../models/enums/wish-list-type';
import { accountSelector, userSelector } from '../../store/configuration-selectors';
import { fetchWishListDetails } from '../../store/whish-list-actions';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { wishListDetailSelector, wishListsSelector } from '../../store/whish-list-selectors';

const StyledContainer = styled(Container)(({ theme }) => ({
    marginBottom: theme.spacing(10),
    [theme.breakpoints.down('sm')]: {
        marginBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
    },
}));

export const WishListPage = (): ReactElement => {
    const dispatch = useAppDispatch();
    const wishList = useAppSelector(wishListDetailSelector);
    const wishLists = useAppSelector(wishListsSelector);
    const { id: paramId = '' } = useParams<{ id?: string }>();
    const wishListId = +paramId || 'default';
    const [editWishList, setEditWishList] = useState<WishListListItem | null>(null);
    const user = useAppSelector(userSelector);
    const isAdmin = hasRole(UserRole.ShopAdmin, user);
    const currentAccount = useAppSelector(accountSelector);

    useEffect(() => {
        dispatch(fetchWishListDetails(wishListId));
    }, [dispatch, wishListId]);

    const requesting = !wishList;
    const empty = !requesting && wishList.suppliers.length === 0;

    const handleEditWishlist = (id: WishListId): void => {
        const list = wishLists.find(list => list.id === id) ?? null;
        setEditWishList(list);
    };

    const canEdit = !!wishList?.isOwner || (isAdmin && wishList?.customerAccount?.id === currentAccount?.id);
    const isShared = wishList?.isSharedWithOwnAccounts || wishList?.isSharedWithOtherAccountUsers;
    const isSharedWithMultiple = isShared && wishList?.sharedAccounts.length > 1;

    function renderWarning(): ReactNode {
        if (isSharedWithMultiple) {
            return (
                <PoolBanner severity="warning" sx={{ mt: 3 }}>
                    <FormattedMessage
                        id="app.wishList.shareInfo.multiple"
                        values={{ count: wishList?.sharedAccounts.length }}
                    />
                </PoolBanner>
            );
        }

        return (
            <PoolBanner severity="warning" sx={{ mt: 3 }}>
                <FormattedMessage id="app.wishList.shareInfo.one" />
            </PoolBanner>
        );
    }

    return (
        <>
            <PoolPageHeader variant="white">
                <PoolRow alignItems="baseline">
                    <FormattedMessage id="app.wishList.title" />
                </PoolRow>
            </PoolPageHeader>
            <WishListSelection onEditWishList={handleEditWishlist} />
            {empty && <WishListEmpty />}
            {!requesting && !empty && (
                <StyledContainer maxWidth="xl">
                    {wishListId !== 'default' && canEdit && isShared && renderWarning()}
                    {wishList?.infoText && <PoolBanner sx={{ mt: canEdit ? 2 : 3 }}>{wishList.infoText}</PoolBanner>}
                    <WishListTopActionBar canEdit={canEdit} onEditWishList={handleEditWishlist} />
                    {wishList.suppliers.map(supplier => (
                        <SupplierTable key={supplier.id} supplier={supplier} />
                    ))}
                </StyledContainer>
            )}

            <WishListModal wishList={editWishList} open={!!editWishList} onClose={() => setEditWishList(null)} />
        </>
    );
};
